//librerías
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faMagnifyingGlass,
  faRightFromBracket,
  faUser,
  faRepeat,
} from "@fortawesome/free-solid-svg-icons";
import {
  Button,
  ButtonGroup,
  Container,
  Nav,
  Navbar,
  NavDropdown,
  Modal,
  Form,
  Spinner,
  Badge,
} from "react-bootstrap";
import { Link, Outlet } from "react-router-dom";
import { useContext, useState, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import { PlataformasxUsuarioInterface } from "../../interfaces/PlataformasxUsuario";
import { API } from "../../global/global";
import axios from "axios";
import { toast } from "react-toastify";

// Custom hooks
import useServiceNotifications from "../../hooks/useServiceNotifications";

//estilos
const classes = require("./Main.module.css").default;

interface Props {
  showModal: boolean;
  handleCloseModal: () => void;
  titulo: string;
}

/**
 * Screen Main
 * @description: Es la pantalla que contiene el nav de la aplicación.
 * @date 14/11/2022.
 * @returns JSX del Screen.
 */

const Main = () => {
  const authContext = useContext(AuthContext);
  const [showModal, setShowModal] = useState(false);
  const [plataformasDisponibles, setPlataformasDisponibles] = useState<PlataformasxUsuarioInterface[]>([]);
  const [plataformaSeleccionada, setPlataformaSeleccionada] = useState<PlataformasxUsuarioInterface | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchPlataformasUsuario = async () => {
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/plataformas_usuario`,
        { usuario_id: authContext.usuario?.id },
        { headers: { Authorization: `Bearer ${authContext.token}` } }
      );
      const todasPlataformas: PlataformasxUsuarioInterface[] = response.data.data;
      const asignadas = todasPlataformas.filter((p: any) => p.asignado === true || p.pivot);
      setPlataformasDisponibles(asignadas.length > 0 ? asignadas : authContext.plataformas);
      setPlataformaSeleccionada(authContext.plataformas[0] || null);
    } catch (error) {
      console.error("Error fetching plataformas:", error);
      toast.error("Error al cargar plataformas");
    } finally {
      setLoading(false);
    }
  };

  const handleSavePlataforma = () => {
    if (!plataformaSeleccionada) {
      toast.error("Debe seleccionar una plataforma");
      return;
    }
    // Reordenar: la seleccionada primero, las demás después
    const reordenadas = [
      plataformaSeleccionada,
      ...authContext.plataformas.filter(p => p.id !== plataformaSeleccionada.id)
    ];
    authContext.updatePlatformas(reordenadas);
    setShowModal(false);
  };

  const handleShowModal = () => {
    fetchPlataformasUsuario();
    setShowModal(true);
  };
  
  const {usuario} = useContext(AuthContext);

  // El rol "Servicios" (cliente) tiene una vista restringida: solo Agregar
  // Servicio (formulario básico), Capturados y Perfil.
  const esRolServicios = usuario?.rol?.nombre === "Servicios";

  // Inicializar notificaciones automáticas
  useServiceNotifications();

  return (
    <>
      {/**Navbar */}
      <Navbar
        bg="light"
        variant="light"
        style={{
          position: "fixed",
          top: 0,
          width: "100%",
          zIndex: 2,
          background:
            "linear-gradient(135deg, rgba(113,0,0,1) 0%, rgba(248,249,250,1) 10%, rgba(248,249,250,1) 100%)",
        }}
      >
        <Container>
          <Navbar.Brand href="/main">
            <img
              src={require("../../img/nmp.png")}
              alt="NMP"
              width={"50"}
              height={"40"}
            />
            <img
              src={require("../../img/skyangel.png")}
              alt="SkyTech"
              width={"35"}
              height={"30"}
            />
            SkyTech
          </Navbar.Brand>
          
          {/* Badge de plataforma activa */}
          <div className="d-flex align-items-center gap-2 me-3">
            {authContext.plataformas.length > 0 ? (
              <Badge bg="dark">{authContext.plataformas[0].nombre}</Badge>
            ) : (
              <Badge bg="warning" text="dark">Sin plataforma</Badge>
            )}
            {authContext.plataformas.length > 1 && (
              <Button
                variant="outline-dark"
                size="sm"
                onClick={handleShowModal}
                title="Cambiar plataforma"
              >
                <FontAwesomeIcon icon={faRepeat as IconProp} />
              </Button>
            )}
          </div>
          
          <Nav className="me-auto">
            {esRolServicios ? (
              // Vista restringida para el rol "Servicios" (cliente)
              <NavDropdown
                style={{ marginRight: "20px" }}
                title="Servicios"
                menuVariant="dark"
              >
                <Link
                  className={classes.dropdownItem}
                  to="servicios/crear-basico"
                >
                  Agregar Servicio
                </Link>
                <Link
                  className={classes.dropdownItem}
                  to="servicios/capturados"
                >
                  Capturados
                </Link>
              </NavDropdown>
            ) : (
              <>
                <Link to="" className={classes.navlink}>
                  Dashboard
                </Link>
                <NavDropdown
                  style={{ marginRight: "20px" }}
                  title="Servicios"
                  menuVariant="dark"
                >
                  <Link className={classes.dropdownItem} to="servicios/capturados">
                    Capturados
                  </Link>
                  <Link className={classes.dropdownItem} to="servicios/programados">
                    Programados
                  </Link>
                  <Link className={classes.dropdownItem} to="servicios/por_firmar">
                    Por firmar
                  </Link>
                  <Link className={classes.dropdownItem} to="servicios/atendidos">
                    Atendidos
                  </Link>
                  <Link className={classes.dropdownItem} to="servicios/en_falso">
                    Salidas en Falso
                  </Link>
                </NavDropdown>
                {authContext.tokenCan("Usuario:show") && (
                  <Link className={classes.navlink} to="catalogos">
                    Catálogos
                  </Link>
                )}
                <NavDropdown title="Administración" menuVariant="dark">
                  {authContext.tokenCan("Cobro:store") && (
                    <Link className={classes.dropdownItem} to="servicios/cobrar">
                      Cobros
                    </Link>
                  )}
                  {authContext.tokenCan("Cobro:store") && (
                    <Link className={classes.dropdownItem} to="servicios/cobrados">
                      Servicios cobrados
                    </Link>
                  )}
                  {authContext.tokenCan("Reportes:download") && (
                    <Link className={classes.dropdownItem} to="reportes">
                      Reportes
                    </Link>
                  )}
                  {authContext.tokenCan("Pago:store") && (
                    <Link className={classes.dropdownItem} to="servicios/pagar">
                      Pagar a Proveedores
                    </Link>
                  )}
                  {authContext.tokenCan("Pago:store") && (
                    <Link className={classes.dropdownItem} to="servicios/pagados">
                      Proveedores con pago
                    </Link>
                  )}
                  {authContext.tokenCan("Usuario:show") && (
                    <Link className={classes.dropdownItem} to="selfies">
                      Selfies
                    </Link>
                  )}
                </NavDropdown>
              </>
            )}
          </Nav>
          {!esRolServicios && (
            <ButtonGroup className="mb-2">
              <Button
                variant="outline-primary"
                style={{ float: "right" }}
                title="Búsqueda General"
              >
                <Link className={classes.linkInButton} to="busqueda">
                  <FontAwesomeIcon icon={faMagnifyingGlass as IconProp} /> Búsqueda
                </Link>
              </Button>
            </ButtonGroup>
          )}
          
          <NavDropdown
            title={
              <>
                <FontAwesomeIcon icon={faUser as IconProp} />{" "}
                {usuario?.nombre} {usuario?.apellido_paterno}
              </>
            }
            id="user-dropdown"
            align="end"
            menuVariant="light"
            className={classes.userDropdown}
          >
            <Link className={classes.dropdownItem} to="perfil">
              <FontAwesomeIcon icon={faUser as IconProp} /> Mi Perfil
            </Link>
            <NavDropdown.Divider />
            <NavDropdown.Item onClick={authContext.logout}>
              <FontAwesomeIcon icon={faRightFromBracket as IconProp} /> Cerrar Sesión
            </NavDropdown.Item>
          </NavDropdown>
        </Container>
      </Navbar>
      
      <Outlet />

      {/* Modal de selección de plataforma activa */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Seleccionar Plataforma Activa</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center">
              <Spinner animation="border" />
            </div>
          ) : (
            <Form>
              {plataformasDisponibles.map((plataforma: PlataformasxUsuarioInterface) => (
                <Form.Check
                  key={plataforma.id}
                  type="radio"
                  name="plataformaActiva"
                  id={`plataforma-${plataforma.id}`}
                  label={plataforma.nombre}
                  checked={plataformaSeleccionada?.id === plataforma.id}
                  onChange={() => setPlataformaSeleccionada(plataforma)}
                  className="mb-2"
                />
              ))}
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSavePlataforma}
            disabled={!plataformaSeleccionada}
          >
            Aplicar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Main;
